import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Box, IconButton, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Pagination, Avatar } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Table from "../../components/Table/Table";
import ProductEditCard from "../../components/ProductEditCard/ProductEditCard";
import CreateProductForm from "../../components/CreateProductForm/CreateProductForm";
import {
  ExpandedImageOverlay,
  ExpandedImageContainer,
  ExpandedImage,
  CarouselButton,
  ImageCounter,
} from "../../components/ProductInfo/ProductInfo.styles";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import {
  getProducts,
  getProductStats,
  updateProduct,
  deleteProduct,
  createProduct,
  exportProductsCsv,
} from "../../api/api";
import { styled } from "@mui/system";
import {useToast} from '../../components/ToastProvider/ToastProvider';

const PAGE_SIZE = 20;

const ProductImage = styled(Avatar)(() => ({
  width: "64px",
  height: "64px",
  borderRadius: "8px",
  backgroundColor: "#eee",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
}));

export default function AdminPortalTableContainer({ filters }) {
  const { showToast } = useToast(); 
  const shownErrors = useRef(new Set());

  const safeToast = (message, type) => {
    if (!shownErrors.current.has(message)) {
      showToast(message, type);
      shownErrors.current.add(message);
      setTimeout(() => shownErrors.current.delete(message), 3000);
    }
  };

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(null);
  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [expandedImageOpen, setExpandedImageOpen] = useState(false);
  const [expandedImageData, setExpandedImageData] = useState(null);
  const [expandedImageIndex, setExpandedImageIndex] = useState(0);
  const [exporting, setExporting] = useState(false);

  const totalPages =
    totalProducts == null ? 1 : Math.max(1, Math.ceil(totalProducts / PAGE_SIZE));

  const filterQS = useMemo(() => {
    const p = new URLSearchParams();
    if (filters?.search) p.set("search", filters.search);
    if (filters?.category) p.set("category", filters.category);
    if (filters?.mfr) p.set("manufacturer", filters.mfr); 
    if (filters?.vendor) p.set("vendor", filters.vendor);
    if (filters?.condition) p.set("condition", filters.condition);
    if (filters?.stock === "in") p.set("in_stock", "true");
    if (filters?.stock === "out") p.set("in_stock", "false");
    // Always include status, even if empty string (to show all products)
    if (filters?.status !== undefined) p.set("status", filters.status);
    return p.toString();
  }, [filters]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    const skip = (page - 1) * PAGE_SIZE;
    params.set("skip", String(skip));
    params.set("limit", String(PAGE_SIZE));
    if (filterQS) {
      for (const [k, v] of new URLSearchParams(filterQS).entries()) {
        params.set(k, v);
      }
      // Handle status filter for active/archived products
      const statusFromQS = new URLSearchParams(filterQS).get('status');
      console.log('🔍 Filter Debug:', { 
        filters, 
        filterQS, 
        statusFromQS, 
        status: filters?.status 
      });
      
      if (statusFromQS === 'archived') {
        params.set("status", "archived");
        params.set("show_archived", "true");
        console.log('📋 Setting status=archived and show_archived=true for archived');
      } else if (statusFromQS === 'active') {
        // Explicitly show only active products
        params.set("show_archived", "false");
        console.log('📋 Setting show_archived=false for active');
      } else if (statusFromQS === 'out_of_stock') {
        // Out of stock filter - status parameter is already set, no need for show_archived
        // Backend will handle stock_quantity == 0 filter
        console.log('📋 Out of stock filter selected');
      } else if (statusFromQS === '' || !statusFromQS) {
        // "All Products" - show only active products (exclude archived)
        params.set("status", "active");
        params.set("show_archived", "false");
        console.log('📋 Setting status=active for all products (excluding archived)');
      }
      
      // Debug: Check if show_archived parameter is set
      console.log('🔍 show_archived parameter:', params.get("show_archived"));
    }
    const finalQueryString = params.toString();
    console.log('🌐 Final API Query:', finalQueryString);
    return finalQueryString;
  }, [page, filterQS, filters?.status]);

  const loadTotal = useCallback(async () => {
    try {
      const stats = await getProductStats(filterQS);
      setTotalProducts(stats?.total_products ?? 0);
    } catch (e) {
      console.error(e);
      safeToast("Failed to load product stats", "error");
      setTotalProducts((prev) => prev ?? 0);
    }
  }, [filterQS]);

  const loadPage = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getProducts(queryString);
      setRows(data || []);
    } catch (e) {
      console.error(e);
      safeToast("Failed to load products", "error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  const handleExportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams(filterQS);
      const statusVal = params.get("status");
      if (statusVal === "archived") {
        params.set("show_archived", "true");
      } else if (!statusVal || statusVal === "") {
        params.set("status", "active");
        params.set("show_archived", "false");
      }
      const blob = await exportProductsCsv(params.toString());
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `products_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      safeToast("Products exported successfully", "success");
    } catch (e) {
      console.error(e);
      safeToast("Failed to export products", "error");
    } finally {
      setExporting(false);
    }
  }, [filterQS]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    loadTotal();
  }, [loadTotal]);

  useEffect(() => {
    const openCreate = () => {
      setEditingProduct({});
      setOpen(true);
    };
    window.addEventListener("apd:create-product", openCreate);
    return () => window.removeEventListener("apd:create-product", openCreate);
  }, []);

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleEditClick = (product) => {
    console.log('AdminPortalTableContainer - Product data for editing:', product);
    setEditingProduct(product);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingProduct(null);
  };

  const handleCloseWithConfirmation = () => {
    setShowCloseConfirmation(true);
  };

  const handleConfirmClose = () => {
    setShowCloseConfirmation(false);
    handleClose();
  };

  const handleCancelClose = () => {
    setShowCloseConfirmation(false);
  };

  const showConfirmToast = (message) => {
    return new Promise((resolve) => {
      const confirmed = window.confirm(message);
      resolve(confirmed);
    });
  };

  const handleDelete = async (product) => {
    const confirmed = await showConfirmToast(`Delete "${product.category}"? This cannot be undone.`);
    if (!confirmed) return;

    try {
      await deleteProduct(product.id);
      safeToast(`Deleted product "${product.category}"`, "success");
      const postDeleteCount = rows.length - 1;
      if (postDeleteCount === 0 && page > 1) setPage((p) => p - 1);
      await loadPage();
      await loadTotal();
    } catch (e) {
      console.error(e);
      safeToast(e.message || "Failed to delete product", "error");
    }
  };

  const handleApprove = async (updatedProduct) => {
    try {
      if (updatedProduct?.id) {
        const ALLOWED = [
          "brand","name","description","sku","category","condition","stock_quantity","weight",
          "dimensions","location","concatenate_code","cost_price","retail_price",
          "current_price","image_url","product_url","category_id","is_available","specifications",
          "sub_part_number","sub_2_part_number",
        ];
        const payload = {};
        for (const k of ALLOWED) if (updatedProduct[k] !== undefined) payload[k] = updatedProduct[k];
        if (payload.retail_price != null && payload.current_price == null) payload.current_price = payload.retail_price;
        const saved = await updateProduct(updatedProduct.id, payload);
        setRows((prev) => prev.map((r) => (r.id === saved.id ? saved : r)));
        safeToast("Product updated successfully", "success");
      } else {
        const ALLOWED_CREATE = [
          "brand","name","description","sku","category","condition","stock_quantity","weight",
          "dimensions","location","concatenate_code","cost_price","retail_price",
          "current_price","image_url","product_url","category_id","is_available","specifications",
          "sub_part_number","sub_2_part_number",
        ];
        const createBody = {};
        for (const k of ALLOWED_CREATE) if (updatedProduct[k] !== undefined) createBody[k] = updatedProduct[k];
        if (createBody.retail_price != null && createBody.current_price == null) createBody.current_price = createBody.retail_price;
        const created = await createProduct(createBody);
        if (page === 1) setRows((prev) => [created, ...prev].slice(0, PAGE_SIZE));
        else await loadPage();
        await loadTotal();
        safeToast("Product created successfully", "success");
      }
      handleClose();
    } catch (e) {
      console.error(e);
      safeToast(e.message || "Save failed", "error");
    }
  };

  const handleFieldChange = (key, value) => {
    setEditingProduct((prev) => ({ ...prev, [key]: value }));
  };

  // Get valid images from product
  const getValidImages = (product) => {
    const validImages = [];
    
    if (product.image_urls && Array.isArray(product.image_urls) && product.image_urls.length > 0) {
      const validFromArray = product.image_urls.filter(
        (url) => url && url.trim() && !url.includes('""') && url !== 'null' && url !== 'undefined' && url !== 'None'
      );
      validImages.push(...validFromArray);
    } else if (product.image_url && product.image_url.trim() && product.image_url !== 'null' && product.image_url !== 'undefined' && product.image_url !== 'None') {
      validImages.push(product.image_url.trim());
    }
    
    if (validImages.length === 0) {
      return ["/ProductHolder.png"];
    }
    
    return validImages;
  };

  const handleImageClick = (product) => {
    const images = getValidImages(product);
    setExpandedImageData({ images, product });
    setExpandedImageIndex(0);
    setExpandedImageOpen(true);
  };

  const handleExpandedImageClose = () => {
    setExpandedImageOpen(false);
    setExpandedImageData(null);
    setExpandedImageIndex(0);
  };

  const goToPreviousExpandedImage = (e) => {
    e.stopPropagation();
    if (expandedImageData) {
      setExpandedImageIndex((prev) => 
        prev === 0 ? expandedImageData.images.length - 1 : prev - 1
      );
    }
  };

  const goToNextExpandedImage = (e) => {
    e.stopPropagation();
    if (expandedImageData) {
      setExpandedImageIndex((prev) => 
        prev === expandedImageData.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  // Keyboard navigation for expanded image viewer
  useEffect(() => {
    if (!expandedImageOpen || !expandedImageData) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleExpandedImageClose();
      } else if (e.key === 'ArrowLeft' && expandedImageData.images.length > 1) {
        e.preventDefault();
        setExpandedImageIndex((prev) => 
          prev === 0 ? expandedImageData.images.length - 1 : prev - 1
        );
      } else if (e.key === 'ArrowRight' && expandedImageData.images.length > 1) {
        e.preventDefault();
        setExpandedImageIndex((prev) => 
          prev === expandedImageData.images.length - 1 ? 0 : prev + 1
        );
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [expandedImageOpen, expandedImageData]);

  const columns = [
    { id: "select", label: "" },
    { id: "image", label: "Image" },
    { id: "description", label: "Description" },
    { id: "category", label: "Category" },
    { id: "brand", label: "MFR" },
    { id: "vendor", label: "Vendor" },
    { id: "sku", label: "Part #" },
    { id: "condition", label: "Condition" },
    { id: "stock_quantity", label: "Stock" },
    { id: "cost_price", label: "Unit Cost Price" },
    { id: "retail_price", label: "Unit Retail Price" },
    { id: "actions", label: "Actions" },
  ];

  const tableRows = rows.map((row) => ({
    ...row,
    select: (
      <Checkbox checked={selected.includes(row.id)} onChange={() => handleSelect(row.id)} />
    ),
    image: (() => {
      // Get the first valid image from image_urls array, fallback to image_url, then placeholder
      const getProductImage = (product) => {
        if (product.image_urls && product.image_urls.length > 0) {
          // Return the first image from the array
          return product.image_urls[0];
        }
        if (product.image_url) {
          return product.image_url;
        }
        return "/ProductHolder.png";
      };

      return (
        <Avatar
          variant="square"
          src={getProductImage(row)}
          alt={row.category}
          onError={(e) => (e.target.src = "/ProductHolder.png")}
          onClick={() => handleImageClick(row)}
          sx={{
            width: "64px",
            height: "64px",
            borderRadius: "8px",
            backgroundColor: "#eee",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            cursor: "pointer",
            transition: "transform 0.2s ease",
            "&:hover": {
              transform: "scale(1.1)",
            },
          }}
        />
      );
    })(),
    description: (
      <Typography 
        variant="body2" 
        sx={{ 
          maxWidth: '300px',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          overflowWrap: 'break-word'
        }}
      >
        {row.description || "—"}
      </Typography>
    ),
    cost_price: (
      <Typography variant="body2">
        {row.cost_price != null ? `$${Number(row.cost_price).toFixed(2)}` : "—"}
      </Typography>
    ),
    retail_price: (
      <Typography variant="body2">
        {row.retail_price != null ? `$${Number(row.retail_price).toFixed(2)}` : "—"}
      </Typography>
    ),
    actions: (
      <Box display="flex" gap={1}>
        <IconButton size="small" onClick={() => handleEditClick(row)}>
          <EditIcon />
        </IconButton>
        <IconButton size="small" onClick={() => handleDelete(row)}>
          <DeleteIcon />
        </IconButton>
      </Box>
    ),
  }));

  return (
    <Box padding={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          {totalProducts != null ? `${totalProducts} product${totalProducts !== 1 ? 's' : ''}` : ''}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon />}
          disabled={exporting || !totalProducts}
          onClick={handleExportCsv}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </Box>
      <Table title="Product Inventory" columns={columns} rows={tableRows} loading={loading} />
      <Box mt={2} display="flex" justifyContent="center">
        <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
      </Box>
      <Dialog 
        open={open} 
        onClose={(event, reason) => {
          // Check if it's create mode
          const isCreateMode = !editingProduct || (editingProduct && Object.keys(editingProduct).length === 0);
          
          if (isCreateMode) {
            // Create mode: Completely prevent closing on backdrop click or escape key
            // Return early without doing anything - this prevents the dialog from closing
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
              return;
            }
          } else {
            // Edit mode - show confirmation for backdrop click or escape key
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
              handleCloseWithConfirmation();
            }
          }
        }}
        maxWidth="lg" 
        fullWidth
        BackdropProps={{
          onClick: (e) => {
            // Check if it's create mode
            const isCreateMode = !editingProduct || (editingProduct && Object.keys(editingProduct).length === 0);
            if (isCreateMode) {
              // Prevent backdrop click from closing in create mode
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
            }
          },
          sx: {
            pointerEvents: (!editingProduct || (editingProduct && Object.keys(editingProduct).length === 0)) 
              ? 'none' 
              : 'auto'
          }
        }}
        PaperProps={{
          sx: { 
            borderRadius: 2,
            maxHeight: '90vh',
            width: '95%',
            maxWidth: '1400px'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1,
          px: 3,
          pt: 2.5
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editingProduct && Object.keys(editingProduct).length > 0 ? "Edit Product" : "Create a New Product"}
          </Typography>
          {/* Close button for both edit and create mode */}
          <IconButton 
            onClick={() => {
              const isCreateMode = !editingProduct || (editingProduct && Object.keys(editingProduct).length === 0);
              if (isCreateMode) {
                // For create mode, close directly without confirmation
                handleClose();
              } else {
                // For edit mode, show confirmation
                handleCloseWithConfirmation();
              }
            }}
            sx={{ 
              color: 'text.secondary',
              '&:hover': { 
                color: 'text.primary',
                backgroundColor: 'action.hover'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'auto' }}>
          {editingProduct && Object.keys(editingProduct).length > 0 ? (
            <ProductEditCard
              product={editingProduct}
              onApprove={handleApprove}
              onDelete={handleDelete}
              onChange={handleFieldChange}
            />
          ) : (
            <CreateProductForm
              onCreated={async (saved) => {
                // Refresh the product list after creation
                await loadPage();
                await loadTotal();
                // Close the dialog
                handleClose();
              }}
              onCancel={() => {
                // Directly close without confirmation for create form
                handleClose();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Close Confirmation Dialog */}
      <Dialog 
        open={showCloseConfirmation} 
        onClose={handleCancelClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Confirm Close
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to close? Any unsaved changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCancelClose} 
            color="secondary"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmClose} 
            color="primary" 
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Expanded Image Viewer */}
      {expandedImageOpen && expandedImageData && (
        <ExpandedImageOverlay onClick={handleExpandedImageClose}>
          <ExpandedImageContainer
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              onClick={handleExpandedImageClose}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 10,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            {expandedImageData.images.length > 1 && (
              <>
                <CarouselButton
                  left
                  onClick={goToPreviousExpandedImage}
                  aria-label="Previous image"
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    },
                  }}
                >
                  <ChevronLeftIcon fontSize="large" />
                </CarouselButton>

                <CarouselButton
                  right
                  onClick={goToNextExpandedImage}
                  aria-label="Next image"
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    },
                  }}
                >
                  <ChevronRightIcon fontSize="large" />
                </CarouselButton>

                <ImageCounter
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                  }}
                >
                  {expandedImageIndex + 1} / {expandedImageData.images.length}
                </ImageCounter>
              </>
            )}

            <ExpandedImage
              src={expandedImageData.images[expandedImageIndex] || expandedImageData.images[0]}
              alt={expandedImageData.product?.description || expandedImageData.product?.category || "Product Image"}
              onClick={(e) => e.stopPropagation()}
            />
          </ExpandedImageContainer>
        </ExpandedImageOverlay>
      )}
    </Box>
  );
}
